import {
  Button,
  Tooltip,
  Typography,
  IconWrapper,
  InfoPanel,
} from "@king-ict/eupisi-ui/components";
import { ReactComponent as InfoIcon } from "@king-ict/eupisi-ui/components/Icon/Info.svg";
import { CodeBook } from "@king-ict/eupisi-ui/models";
import { hrDateWithTime } from "@king-ict/eupisi-ui/utils";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import { useTypedSelector } from "../../app/rootReducer";
import { useBusinessRule } from "../../hooks/businessRule/useBusinessRule";
import { EnrollmentCyclePedagogicalYear } from "../../models/EnrollmentCycle/EnrollmentCyclePedagogicalYear";
import EnrollmentTypeEnum from "../../models/EnrollmentType/EnrollmentTypeEnum";
import PedagogicalYear from "../../models/PedagogicalYear/PedagogicalYear";
import Person from "../../models/Person/Person";
import ChildRequestsCount from "../../models/Request/ChildRequestsCount";
import RequestListItem from "../../models/Request/RequestListItem";
import RequestStatusEnum from "../../models/Request/RequestStatusEnum";
import RequestTypeEnum from "../../models/Request/RequestTypeEnum";
import { newRequestUrl } from "../../routes/routes";
import ChildRequestList from "./ChildRequestList";
import styles from "./DashboardList.module.scss";

interface Props {
  pedagogicalYear: PedagogicalYear;
  pedagogicalYearChildren: Person[];
  pedagogicalYearRequests: RequestListItem[];
  currentImportantDate?: Date;
  childrenRequestsCount?: ChildRequestsCount[];
  months: CodeBook[];
  enrollmentCyclesForPedagogicalYear: EnrollmentCyclePedagogicalYear[];
}

export default function DashboardList({
  pedagogicalYear,
  pedagogicalYearChildren,
  pedagogicalYearRequests,
  currentImportantDate,
  childrenRequestsCount,
  months,
  enrollmentCyclesForPedagogicalYear,
}: Props) {
  const { t } = useTranslation();
  const history = useHistory();
  const {
    isRequestCreationAllowed,
    isMaximumRequestCountReached,
    isPreschoolRequestAllowed,
  } = useBusinessRule();

  const currentImportantEvent = useTypedSelector(
    (state) => state.pedagogicalYear.currentImportantDate
  );

  const regularTerm = useTypedSelector(
    (state) => state.pedagogicalYear.regularTerm
  );

  const PedagogicalYearData = () => {
    return (
      <Col xs={12}>
        <Typography component="h1" className={styles.title}>
          {t("common.pedagogicalYear")} {pedagogicalYear.name}
        </Typography>
      </Col>
    );
  };

  function HrDate(date: Date) {
    return new Date(date).toLocaleDateString("hr-HR");
  }

  function HrTime(dateString: string): string {
    var date: Date = new Date(dateString);
    var hours: number = date.getHours();
    var minutes: number = date.getMinutes();

    // Formatting hours and minutes to two digits
    var formattedHours: string = hours.toString().padStart(2, "0");
    var formattedMinutes: string = minutes.toString().padStart(2, "0");

    var timeString: string = formattedHours + ":" + formattedMinutes;
    return timeString;
  }

  const RegularTermData = () => {
    return (
      <>
        {pedagogicalYear.isActive && regularTerm && regularTerm.isRegularTerm && (
          <Col xs={12} className={styles.regularTermCol}>
            <div className={styles.regularTermContent}>
              <div className={styles.regularTermIcon}>
                <IconWrapper color="success">
                  <InfoIcon />
                </IconWrapper>
              </div>

              <div className={styles.regularTermText}>
                <Typography color="success" className={styles.marginButtom3}>
                  <b>
                    {t("dashboard.RegularTermHeader")}{" "}
                    {t("dashboard.RegularTermText", {
                      dateFrom: HrDate(regularTerm.dateTimeFrom),
                      timeFrom: HrTime(regularTerm.dateTimeFrom.toString()),
                      dateTo: HrDate(regularTerm.dateTimeTo),
                      timeTo: HrTime(regularTerm.dateTimeTo.toString()),
                    })}
                  </b>
                </Typography>
                <Typography color="success" className={styles.marginButtom3}>
                  {t("dashboard.RegularTermTextHelper")}{" "}
                  <b>{t("dashboard.RegularTermTextHelperStatus")}</b>
                </Typography>
              </div>
            </div>
          </Col>
        )}
      </>
    );
  };

  interface ChildDataProps {
    child: Person;
    months: CodeBook[];
  }

  const ChildData = ({ child, months }: ChildDataProps) => {
    const childRequests = pedagogicalYearRequests
      .filter((r) => r.child.oib === child.oib)
      .sort((a, b) => a.priority - b.priority);

    //let preschoolRequestCount = 0;
    const childCountInfo = childrenRequestsCount?.find(
      (cr) => cr.childOib === child.oib
    );

    /*if (childCountInfo) {
      preschoolRequestCount = childCountInfo.preschoolRequestsCount;
    }*/

    return (
      <>
        <Col xs={12} md={6}>
          <Typography variant="smallHeading" component="h2">
            {t("dashboard.childNameLabel")}
          </Typography>
          <p className={styles.childName}>
            {child.firstName} {child.lastName}
          </p>
          <p className={styles.childOib}>{`${t("person.oib")}: ${
            child.oib
          }`}</p>
        </Col>
        {pedagogicalYear.isActive &&
          currentImportantEvent.enrollmentTypeId ===
            EnrollmentTypeEnum.RegularEnrollment && (
            <Col xs={12} md={6} className={`${styles.actionContainer}`}>
              {isPreschoolRequestAllowed(child.dateOfBirth) && (
                <Col
                  xs={12}
                  md={12}
                  className={`${styles.actionContainer} ${styles.actionMargin}`}
                >
                  {/*<Tooltip*/}
                  {/*  placement="bottom"*/}
                  {/*  overlay={*/}
                  {/*    isRequestCreationAllowed()*/}
                  {/*      ? isMaximumRequestCountReached(*/}
                  {/*          preschoolRequestCount,*/}
                  {/*          RequestTypeEnum.Preschool*/}
                  {/*        ) || childCountInfo == null*/}
                  {/*        ? t("dashboard.createRequestInfoDisabled")*/}
                  {/*        : t("dashboard.createPreschoolRequestInfoEnabled", {*/}
                  {/*            endDate: hrDateWithTime(currentImportantDate!),*/}
                  {/*          })*/}
                  {/*      : t("dashboard.requestCreationNotAllowed")*/}
                  {/*  }*/}
                  {/*>*/}
                  {/*  <IconWrapper color="primary">*/}
                  {/*    <InfoIcon aria-label={t("accessibility.infoIcon")} />*/}
                  {/*  </IconWrapper>*/}
                  {/*</Tooltip>*/}
                  {/*<Button*/}
                  {/*  className={`${styles.actionButton} ${styles.paddingBtn}`}*/}
                  {/*  color="primary"*/}
                  {/*  disabled={*/}
                  {/*    isMaximumRequestCountReached(*/}
                  {/*      preschoolRequestCount,*/}
                  {/*      RequestTypeEnum.Preschool*/}
                  {/*    ) ||*/}
                  {/*    !isRequestCreationAllowed() ||*/}
                  {/*    childCountInfo == null*/}
                  {/*  }*/}
                  {/*  onClick={() =>*/}
                  {/*    history.push(newPreschoolRequestUrl(child.oib))*/}
                  {/*  }*/}
                  {/*>*/}
                  {/*  {t("dashboard.createPreschoolRequestButton")}*/}
                  {/*</Button>*/}
                </Col>
              )}

              <Col xs={12} md={12} className={`${styles.actionContainer}`}>
                <Tooltip
                  placement="bottom"
                  overlay={
                    isRequestCreationAllowed()
                      ? isMaximumRequestCountReached(
                          childCountInfo?.requestsCount ?? 0,
                          RequestTypeEnum.Kindergarten
                        ) || childCountInfo == null
                        ? t("dashboard.createRequestInfoDisabled")
                        : t("dashboard.createRequestInfoEnabled", {
                            endDate: hrDateWithTime(currentImportantDate!),
                          })
                      : t("dashboard.requestCreationNotAllowed")
                  }
                >
                  <IconWrapper color="primary">
                    <InfoIcon aria-label={t("accessibility.infoIcon")} />
                  </IconWrapper>
                </Tooltip>
                <Button
                  className={styles.actionButton}
                  color="primary"
                  disabled={
                    isMaximumRequestCountReached(
                      childCountInfo?.requestsCount ?? 0,
                      RequestTypeEnum.Kindergarten
                    ) ||
                    !isRequestCreationAllowed() ||
                    childCountInfo == null
                  }
                  onClick={() => history.push(newRequestUrl(child.oib))}
                >
                  {t("dashboard.createRequestButton")}
                </Button>
              </Col>
            </Col>
          )}
        {pedagogicalYearRequests != null &&
          pedagogicalYearRequests.length > 0 &&
          pedagogicalYearRequests.some(
            (x) =>
              x.status.id === RequestStatusEnum.Draft &&
              x.child.oib === child.oib
          ) && (
            <Col xs={12}>
              <InfoPanel
                message={t("dashboard.requestsInOpenStatus")}
                color="warning"
              />
            </Col>
          )}
        <Col xs={12} className={styles.childRequestListContainer}>
          <ChildRequestList
            pedagogicalYear={pedagogicalYear}
            childRequests={childRequests}
            isMaximumRequestCountReached={isMaximumRequestCountReached(
              childCountInfo?.requestsCount ?? 0,
              RequestTypeEnum.Kindergarten
            )}
            childOib={child.oib}
            childCountInfoExist={childCountInfo == null ? true : false}
            childDateOfBirth={child.dateOfBirth}
            months={months}
            enrollmentCyclesForPedagogicalYear={
              enrollmentCyclesForPedagogicalYear
            }
          />
        </Col>
      </>
    );
  };

  return (
    <Row className={styles.root}>
      <PedagogicalYearData />
      <RegularTermData />
      {pedagogicalYearChildren.map((c) => (
        <ChildData key={c.oib} child={c} months={months} />
      ))}
    </Row>
  );
}
